import React, { useCallback } from 'react'
import type { ClikArtItem } from '@/api/types/clikart'
import { TrayThumbnailCard } from '@/components/app/panels/right-panel/tray/tray-thumbnail-card'
import { useClikArtPlacementOptional } from '@/contexts/clikart-placement-context'
import { CLIKART_TRAY_DRAGGING_THUMB_OPACITY } from '@/lib/clikart/clikart-placement-config'
import { startTrayDragFromPointer } from '@/lib/placement/start-tray-drag-from-pointer'

type ClikArtThumbnailProps = {
  item: ClikArtItem
  /** Gallery layout: 1 = large single-column card, 2 = compact grid. */
  columns?: 1 | 2
  /** Whether hovering/focusing may open the enlarged preview. Kept apart from `columns`: the
   *  gallery decides it (the single-column card is already large). */
  previewEnabled?: boolean
  /** Show `item.displayName` under the image. */
  showFilename?: boolean
  /** Reports hover/focus preview open state for the host panel's Escape priority chain. */
  onPreviewOpenChange?: (open: boolean) => void
}

/**
 * ClikArt gallery thumbnail: the shared `TrayThumbnailCard` (same component Photos/Backgrounds
 * use for their own tray grids), wired to ClikArt placement — pointer down starts a drag onto the
 * canvas, Enter/Space starts a keyboard carry. Mirrors `photo-tray-thumbnail.tsx`'s
 * `PhotoTrayThumbnail`. Was a bespoke non-interactive card before WMPCM-11014 — that was
 * deliberate at the time (no drag-to-place existed yet); switching to `TrayThumbnailCard` here
 * both adds the drag affordance and picks up its hover-preview/broken-image handling for free.
 */
export function ClikArtThumbnail({
  item,
  columns = 2,
  previewEnabled = true,
  showFilename = false,
  onPreviewOpenChange,
}: ClikArtThumbnailProps) {
  const placement = useClikArtPlacementOptional()
  const isDraggingThis = placement?.draggingArtId === item.artId && placement.isDraggingClikArt

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, card: HTMLDivElement) => {
      if (!placement) return

      startTrayDragFromPointer(e, card, (clientX, clientY, rect, pointerId, _pointerType, el) =>
        placement.beginPointerDrag(item, clientX, clientY, rect, pointerId, el)
      )
    },
    [item, placement]
  )

  const handleActivate = useCallback(
    (rect: DOMRect) => placement?.beginKeyboardCarry(item, rect),
    [item, placement]
  )

  return (
    <TrayThumbnailCard
      itemKey={item.artId}
      label={item.displayName}
      ariaLabel={`${item.displayName}. Press Enter or Space to pick up for placement on canvas.`}
      imageUrl={item.scrUrl}
      previewUrl={item.priUrl || item.scrUrl}
      showCaption={showFilename}
      placed={item.placedFlag}
      placedBadgePlacement="overlap-bottom"
      columns={columns}
      opacity={isDraggingThis ? CLIKART_TRAY_DRAGGING_THUMB_OPACITY : 1}
      previewEnabled={previewEnabled && !placement?.isDraggingClikArt}
      onPointerDown={handlePointerDown}
      onActivate={placement ? handleActivate : undefined}
      onPreviewOpenChange={onPreviewOpenChange}
    />
  )
}
